import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const TableGraph: React.FC = () => {
    const [tableData, setTableData] = useState<any[]>([]);
    const [questions, setQuestions] = useState<any[]>([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [columnNames, setColumnNames] = useState<string[]>([]);
    const [showImage, setShowImage] = useState(false);
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const modal = useRef<HTMLIonModalElement>(null);

    const objects = [
        "Apples",
        "Bananas",
        "Oranges",
        "Grapes",
        "Cars",
        "Trucks",
        "Dogs",
        "Cats",
        "Books",
        "Pens",
        "Chocolates",
        "Bottles",
        "Hats",
        "Shoes",
        "Bags",
        "Pencils",
        "Erasers",
        "Kites",
        "Balls",
        "Lamps",
    ];

    const dynamicColumnNames = [
        "Shop A",
        "Shop B",
        "Shop C",
        "Shop D",
        "Shop E",
        "Shop F",
        "Shop G",
    ];

    // Generate random table data
    const generateTableData = () => {
        const selectedObjects = [];
        const usedIndices = new Set<number>();

        while (selectedObjects.length < 4) {
            const randomIndex = Math.floor(Math.random() * objects.length);
            const column2Value = Math.floor(Math.random() * 50) + 10; // Random value for column 2
            const column3Value = Math.floor(Math.random() * 40) + 20; // Random value for column 3
            const column4Value = Math.floor(Math.random() * 60) + 15; // Random value for column 4

            if (!usedIndices.has(randomIndex)) {
                selectedObjects.push({
                    object: objects[randomIndex],
                    column2: column2Value,
                    column3: column3Value,
                    column4: column4Value,
                });
                usedIndices.add(randomIndex);
            }
        }
        return selectedObjects;
    };

    // Generate random column names
    const generateColumnNames = () => {
        const shuffledNames = [...dynamicColumnNames].sort(() => Math.random() - 0.5);
        return shuffledNames.slice(0, 3);
    };

    // Generate dynamic questions based on the table data
    const generateQuestions = (data: any[], columnNames: string[]) => {
        const totalColumn2 = data.reduce((sum, item) => sum + item.column2, 0);
        const maxColumn2 = Math.max(...data.map((item) => item.column2));
        const maxColumn2Object = data.find((item) => item.column2 === maxColumn2)?.object;

        return [
            {
                question: `How many ${data[0].object} are sold in ${columnNames[0]}?`,
                answer: data[0].column2,
            },
            {
                question: `What is the total count for ${columnNames[0]}?`,
                answer: totalColumn2,
            },
            {
                question: `Which object has the highest count in ${columnNames[0]}?`,
                answer: maxColumn2Object,
            },
            {
                question: `What is the count of ${data[2].object} in ${columnNames[1]}?`,
                answer: data[2].column3,
            },
            {
                question: `What is the total count for ${columnNames[2]}?`,
                answer: data.reduce((sum, item) => sum + item.column4, 0),
            },
        ];
    };

    useEffect(() => {
        handleQuestion()
        document.title = "Data Handling with Tables – UKG Maths Activity | Mama’s Guidance";
    }, []);

    const handleQuestion = () => {
        const columnNames = generateColumnNames();
        const tableData = generateTableData();
        setColumnNames(columnNames);
        setTableData(tableData);
        setQuestions(generateQuestions(tableData, columnNames));
        setCurrentQuestionIndex(Math.floor(Math.random() * 3) + 1);
    };

    const handleAnswerSubmit = (userAnswer: any) => {
        setSelectedOption(userAnswer);
        const correctAnswer = questions[currentQuestionIndex].answer;
        if (userAnswer === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                handleQuestion();
                setShowImage(false);
            }, 5000);
        } else {
            setShowImage(false);
        }
    };



    return (
        <>
            <Helmet>
                <meta name="description" content="Introduce UKG students to data handling using simple tables. Teach sorting, counting, and comparing data with engaging, child-friendly worksheets." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/data-handling/tables" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Table Graph</h1>

                                <p>
                                    Tables are a great way to organize and display information! In this <strong>“Data Handling with Tables”</strong> activity, UKG students learn to read and interpret simple tables to answer questions. This foundational concept builds critical thinking and prepares children for future mathematical concepts.
                                </p>

                                <h2>📋 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>How to collect and sort simple data</li>
                                    <li>Reading and interpreting tables</li>
                                    <li>Counting and comparing entries</li>
                                    <li>Answering questions based on table data</li>
                                </ul>

                                <h2>🎯 Skills Developed</h2>
                                <ul>
                                    <li>Logical reasoning</li>
                                    <li>Visual organization</li>
                                    <li>Early understanding of charts and data</li>
                                    <li>Answering “how many”, “more than”, “less than” type questions</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 Hands-On Activities to Reinforce Learning</h2>
                                <ul>
                                    <li>Count the number of different fruit in a bowl and make a table</li>
                                    <li>Use toy cars of different colors to sort and record in a chart</li>
                                    <li>Ask: “Which is the most/least?” or “How many more red than blue?”</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Table Graph</h1>

                            <p>
                                Tables are a great way to organize and display information! In this <strong>“Data Handling with Tables”</strong> activity, UKG students learn to read and interpret simple tables to answer questions. This foundational concept builds critical thinking and prepares children for future mathematical concepts.
                            </p>

                            <h2>📋 What Will Your Child Learn?</h2>
                            <ul>
                                <li>How to collect and sort simple data</li>
                                <li>Reading and interpreting tables</li>
                                <li>Counting and comparing entries</li>
                                <li>Answering questions based on table data</li>
                            </ul>

                            <h2>🎯 Skills Developed</h2>
                            <ul>
                                <li>Logical reasoning</li>
                                <li>Visual organization</li>
                                <li>Early understanding of charts and data</li>
                                <li>Answering “how many”, “more than”, “less than” type questions</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 Hands-On Activities to Reinforce Learning</h2>
                            <ul>
                                <li>Count the number of different fruit in a bowl and make a table</li>
                                <li>Use toy cars of different colors to sort and record in a chart</li>
                                <li>Ask: “Which is the most/least?” or “How many more red than blue?”</li>
                            </ul>
                        </article>
                        <div>
                            <table style={{ width: "100%", border: "1px solid #ccc", marginBottom: "20px" }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: "8px", border: "1px solid #ccc" }}>Object</th>
                                        {columnNames.map((name, index) => (
                                            <th key={index} style={{ padding: "8px", border: "1px solid #ccc" }}>
                                                {name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.map((row, index) => (
                                        <tr key={index}>
                                            <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.object}</td>
                                            <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column2}</td>
                                            <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column3}</td>
                                            <td style={{ padding: "8px", border: "1px solid #ccc" }}>{row.column4}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <h4>Question</h4>
                            <p>{questions[currentQuestionIndex]?.question}</p>

                            <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                                {typeof questions[currentQuestionIndex]?.answer === "number" ? (
                                    [questions[currentQuestionIndex].answer - 2, questions[currentQuestionIndex].answer, questions[currentQuestionIndex].answer + 2]
                                        .sort(() => Math.random() - 0.5) // Shuffle options
                                        .map((option, idx) => (
                                            <div style={{ margin: "5px", padding: "10px", minWidth: "60px" }} className={`background-blue ${selectedOption === option
                                                ? option === questions[currentQuestionIndex].answer
                                                    ? 'correct'
                                                    : 'incorrect'
                                                : ''
                                                }`}
                                                key={idx} onClick={() => handleAnswerSubmit(option)}>
                                                {option}
                                            </div>
                                        ))
                                ) : (
                                    tableData.map((row, idx) => (
                                        <div style={{ margin: "5px", padding: "10px", minWidth: "60px" }} className={`background-blue ${selectedOption === row.object
                                            ? row.object === questions[currentQuestionIndex].answer
                                                ? 'correct'
                                                : 'incorrect'
                                            : ''
                                            }`}
                                            key={idx} onClick={() => handleAnswerSubmit(row.object)}>
                                            {row.object}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: '150px' }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                            alt="Well Done"
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default TableGraph;
